import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Button,
  TextInput,
  Textarea,
  NumberInput,
  ActionIcon,
  Divider,
  Table,
  Skeleton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconTrash, IconShoppingCart } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import {
  useCart,
  useUpdateCartItem,
  useRemoveCartItem,
  useClearCart,
  useApplyPromo,
  useCheckout,
  useCartNudges,
} from '@/hooks/usePortalApi';
import type { DiscountNudge } from '@/types';

export default function CartPage() {
  const navigate = useNavigate();
  const { data: cart, isLoading } = useCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const clearCart = useClearCart();
  const applyPromo = useApplyPromo();
  const checkout = useCheckout();
  const cartNudges = useCartNudges();
  const [nudges, setNudges] = useState<DiscountNudge[]>([]);

  useEffect(() => {
    if (cart?.items?.length) {
      cartNudges.mutate(
        { items: cart.items.map((i: any) => ({ product_id: i.product_id, quantity: i.quantity, product_name: i.product_name })) },
        { onSuccess: (res) => setNudges(res.data?.nudges ?? []) },
      );
    }
  }, [cart?.items?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [promoCode, setPromoCode] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');

  const handleCheckout = () => {
    checkout.mutate(
      {
        delivery_date: deliveryDate?.toISOString().split('T')[0],
        po_number: poNumber || undefined,
        notes: notes || undefined,
      },
      {
        onSuccess: (res) => {
          notifications.show({
            title: 'Order placed!',
            message: `Order ${res.data.order?.order_number || ''} has been confirmed`,
            color: 'brand',
          });
          navigate(`/orders/${res.data.order?.id}`);
        },
        onError: () => {
          notifications.show({
            title: 'Checkout failed',
            message: 'Please try again',
            color: 'red',
          });
        },
      },
    );
  };

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;
    applyPromo.mutate(promoCode, {
      onSuccess: () => {
        notifications.show({ title: 'Promo applied!', message: `Code "${promoCode}" applied`, color: 'brand' });
      },
      onError: () => {
        notifications.show({ title: 'Invalid promo', message: 'Code is invalid or expired', color: 'red' });
      },
    });
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={200} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!cart?.items?.length) {
    return (
      <Stack gap="lg">
        <PageHeader title="Your Cart" />
        <EmptyState
          icon={<IconShoppingCart size={30} />}
          title="Your cart is empty"
          description="Browse our catalog to find products"
          action={
            <Button onClick={() => navigate('/shop')}>
              Start Shopping
            </Button>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Your Cart"
        actions={
          <Button
            variant="subtle"
            color="red"
            size="xs"
            onClick={() => clearCart.mutate()}
          >
            Clear Cart
          </Button>
        }
      />

      {/* Cart Items Table */}
      <Paper>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th w={100}>Price</Table.Th>
              <Table.Th w={120}>Qty</Table.Th>
              <Table.Th w={100} ta="right">Total</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {cart.items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>{item.product_name}</Text>
                  <Text size="xs" c="dimmed">{item.sku}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">${parseFloat(item.unit_price).toFixed(2)}</Text>
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={item.quantity}
                    onChange={(v) =>
                      updateItem.mutate({ itemId: item.id, quantity: Number(v) || 1 })
                    }
                    min={1}
                    size="xs"
                    w={80}
                  />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" fw={500}>${parseFloat(item.line_total).toFixed(2)}</Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    size="sm"
                    onClick={() => removeItem.mutate(item.id)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Volume Discount Nudges */}
      {nudges.length > 0 && (
        <Paper p="sm" style={{ backgroundColor: 'var(--mantine-color-blue-0)', border: '1px solid var(--mantine-color-blue-2)' }}>
          <Text size="sm" fw={600} mb="xs">Volume Discount Opportunities</Text>
          {nudges.map((n) => (
            <Text key={n.product_id} size="sm" mb={4}>
              Add {n.qty_needed} more of <b>{n.product_name}</b> to save ${n.potential_savings.toFixed(2)} (${n.next_tier_price.toFixed(2)}/unit vs ${n.current_price.toFixed(2)}/unit)
            </Text>
          ))}
        </Paper>
      )}

      <SimpleGridCheckout>
        {/* Promo + Delivery Options */}
        <Paper p="md">
          <Stack gap="sm">
            <Text fw={600}>Order Details</Text>
            <Group gap="xs">
              <TextInput
                placeholder="Promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.currentTarget.value)}
                style={{ flex: 1 }}
                size="sm"
              />
              <Button
                size="sm"
                variant="light"
                onClick={handleApplyPromo}
                loading={applyPromo.isPending}
              >
                Apply
              </Button>
            </Group>
            {cart.promo_code && (
              <Text size="sm" c="brand" fw={500}>
                Promo &quot;{cart.promo_code}&quot; applied
              </Text>
            )}
            <DatePickerInput
              label="Preferred Delivery Date"
              placeholder="Select date"
              value={deliveryDate}
              onChange={setDeliveryDate}
              minDate={new Date()}
              size="sm"
            />
            <TextInput
              label="PO Number"
              placeholder="Optional reference"
              value={poNumber}
              onChange={(e) => setPoNumber(e.currentTarget.value)}
              size="sm"
            />
            <Textarea
              label="Delivery Notes"
              placeholder="Any special instructions?"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              size="sm"
              rows={3}
            />
          </Stack>
        </Paper>

        {/* Order Summary */}
        <Paper p="md">
          <Stack gap="sm">
            <Text fw={600}>Order Summary</Text>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Subtotal ({cart.item_count} items)</Text>
              <Text size="sm">${parseFloat(cart.subtotal).toFixed(2)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Tax</Text>
              <Text size="sm">${parseFloat(cart.tax).toFixed(2)}</Text>
            </Group>
            <Divider />
            <Group justify="space-between">
              <Text fw={700}>Total</Text>
              <Text fw={700} size="xl" c="brand">
                ${parseFloat(cart.total).toFixed(2)}
              </Text>
            </Group>
            <Button
              fullWidth
              size="md"
              onClick={handleCheckout}
              loading={checkout.isPending}
            >
              Place Order
            </Button>
          </Stack>
        </Paper>
      </SimpleGridCheckout>
    </Stack>
  );
}

// Simple two-column layout for checkout section
function SimpleGridCheckout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 'var(--mantine-spacing-md)' }}>
      {children}
    </div>
  );
}
